import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { fetchMinisite } from '../../functions/fetchMinisite';
import language from '../../../utils/LanguageSettings';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';
import RenderSnA from '../../functions/renderSnA';
import Error404 from '../Error404';
import { useMinisiteLayout, HeaderMode, FooterMode } from '../../../contexts/MinisiteLayoutContext';
import '../../../css/minisite.css';

interface Content {
  type: string;
  text?: string;
  children?: Content[];
}

interface MinisiteComponent {
  __component: 'minisite.text-editor' | 'minisite.html-link';
  id: number;
  Content?: string | Content[]; // For minisite.text-editor
  HtmlLink?: string; // For minisite.html-link
}

interface MinisiteData {
  id: number;
  Title: string;
  Slug: string;
  HeaderMenu: 'General' | 'Hidden' | 'Logo & Language';
  FooterMenu: 'General' | 'Hidden' | 'Footer Menu Only';
  MinisiteDetails: MinisiteComponent[];
  SEO: any;
  GA: any[];
}

const toHeaderMode = (value: string): HeaderMode => {
  switch (value) {
    case 'Hidden': return 'hidden';
    case 'Logo & Language': return 'logo-language';
    default: return 'general';
  }
};

const toFooterMode = (value: string): FooterMode => {
  switch (value) {
    case 'Hidden': return 'hidden';
    case 'Footer Menu Only': return 'footer-only';
    default: return 'general';
  }
};

const Minisite: React.FC = () => {
  const { '*': rawSlug } = useParams<{ '*': string }>();
  const slug = rawSlug?.replace(/\/$/, ''); // Remove trailing slash if it exists
  const [minisite, setMinisite] = useState<MinisiteData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { setLayout, resetLayout } = useMinisiteLayout();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchMinisite(slug, language);
        if (response && response.data && response.data.length > 0) {
          const data = response.data[0].attributes;
          setMinisite(data);
          setLayout(toHeaderMode(data.HeaderMenu), toFooterMode(data.FooterMenu));
        } else {
          setError('No data found');
        }
      } catch (err) {
        setError('Failed to fetch data');
      }
    };

    fetchData();

    return () => {
      resetLayout();
    };
  }, [slug, setLayout, resetLayout]);

  if (error) {
    if (error == "No data found"){
      return <Error404 />
    }
    return <div>{error}</div>;
  }

  if (!minisite) {
    return <div>Loading...</div>;
  }

  return (
    <div className="minisite-container">
      <RenderSnA seo={minisite.SEO} ga={minisite.GA} />
      {minisite.MinisiteDetails.map((component, index) => {
        if (component.__component === 'minisite.text-editor' && component.Content) {
          return <TextEditorComponent key={component.id} content={component.Content} index={index} />;
        }
        if (component.__component === 'minisite.html-link' && component.HtmlLink) {
          return <HtmlLinkComponent key={component.id} htmlLink={component.HtmlLink} index={index} />;
        }
        return null;
      })}
    </div>
  );
};

const TextEditorComponent: React.FC<{ content: string | Content[]; index: number }> = ({ content, index }) => {

  const customUrlTransform = (url: string) => {
    if (url.startsWith('tel:')) {
      return url;
    }
    return defaultUrlTransform(url);
  };
  
  return (
    <section id={`section-${index + 1}`} className="module-text-editor">
      <div className="container-fluid support-text-editor">
        {typeof content === 'string' ? (
          <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]} urlTransform={customUrlTransform}>{content}</ReactMarkdown>
        ) : (
          // Fallback for structured text, though Strapi's dynamic zones usually give markdown string.
          <div>
            {content.map((item, index) => (
              <div key={index}>
                {item.type === 'paragraph' && item.children && item.children.map((child, childIndex) => (
                  <p key={childIndex}>{child.text}</p>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const HtmlLinkComponent: React.FC<{ htmlLink: string; index: number }> = ({ htmlLink, index }) => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const contentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const fetchHtml = async () => {
      try {
        const response = await fetch(htmlLink);
        if (!response.ok) throw new Error(`Failed to fetch HTML: ${response.statusText}`);
        const text = await response.text();
        setHtmlContent(text);
      } catch (err) {
        console.error('Error fetching HTML content:', err);
        setHtmlContent('<p>Error: Could not load content.</p>');
      }
    };
    fetchHtml();
  }, [htmlLink]);

  useEffect(() => {
    if (htmlContent && contentRef.current) {
      const container = contentRef.current;
      const scripts = Array.from(container.getElementsByTagName('script'));
      const newScripts: HTMLScriptElement[] = [];

      scripts.forEach(oldScript => {
        const newScript = document.createElement('script');
        // Copy all attributes from the old script to the new one
        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        // If there's inline script content, copy it
        if (oldScript.innerHTML) {
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        }
        document.body.appendChild(newScript);
        newScripts.push(newScript);
      });

      // Cleanup function to remove the dynamically added scripts when the component unmounts or content changes
      return () => newScripts.forEach(script => document.body.removeChild(script));
    }
  }, [htmlContent]);

  return htmlContent ? <section id={`section-${index + 1}`} ref={contentRef} className="html_upload" dangerouslySetInnerHTML={{ __html: htmlContent }} /> : null;
};

export default Minisite;